import { useState } from 'react';
import { Modal, Field } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { toast } from '../../stores/toast.store';
import { EXPORT_SOURCES, runExport, runDashboardExport, runReportExport, type ReportPeriod, type DashboardExportFormat, type TableFormat } from '../../lib/export';

type Format = TableFormat;

/**
 * Export universale: in base al "what" passato, decide cosa fare:
 *  - Dashboard          → snapshot stampabile via window.print
 *  - Report             → dati del report (divisioni/clienti/mensile) per il periodo scelto
 *  - Documenti          → preventivi + fatture uniti
 *  - {Clienti, Fatture, …} → fetch /clients, /invoices, … e CSV/JSON
 *  - default fallback   → mostra "non disponibile" se "what" non è mappato
 */
export function ExportModal({ onClose, what, period }: { onClose: () => void; what: string; period?: ReportPeriod }) {
  const [format, setFormat] = useState<Format>('pdf');
  const [dashFormat, setDashFormat] = useState<DashboardExportFormat>('pdf');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ count: number; filename: string } | null>(null);

  // Casi speciali
  const isDashboard = what === 'Dashboard';
  const isReport = what === 'Report';
  const isDocumenti = what === 'Documenti';
  const source = isDashboard || isReport || isDocumenti ? null : EXPORT_SOURCES[what];
  const isSupported = isDashboard || isReport || isDocumenti || !!source;

  // Calcola il periodo corrente (Q + anno) per la dashboard
  const currentPeriod = (() => {
    const now = new Date();
    return { q: Math.floor(now.getMonth() / 3) + 1, y: now.getFullYear() };
  })();

  const submit = async () => {
    setError(null);
    setBusy(true);
    try {
      if (isDashboard) {
        const r = await runDashboardExport(dashFormat, currentPeriod);
        setResult(r);
        toast.ok('Export pronto', r.filename);
        setTimeout(() => onClose(), 1200);
        return;
      }
      if (isReport) {
        if (!period) throw new Error('Periodo del report mancante');
        const r = await runReportExport(dashFormat, period);
        setResult(r);
        toast.ok('Export pronto', `${r.count} righe · ${r.filename}`);
        setTimeout(() => onClose(), 1200);
        return;
      }
      const r = await runExport(what, format);
      setResult(r);
      toast.ok('Export pronto', `${r.count} record · ${r.filename}`);
      setTimeout(() => onClose(), 1200);
    } catch (e: any) {
      const msg = e?.message ?? 'Errore export';
      setError(msg);
      toast.alert('Errore export', msg);
    } finally {
      setBusy(false);
    }
  };

  // Description card dinamica
  const description = isDashboard
    ? 'Snapshot della dashboard del trimestre corrente: KPI principali, ricavi per divisione, lista progetti attivi e alerts. Disponibile come PDF brandizzato (replica fedele dell\'UI), CSV o JSON dei dati grezzi.'
    : isReport
      ? `Dati del report per il periodo selezionato${period?.label ? ` (${period.label})` : ''}: ricavi e margine per divisione, clienti più profittevoli, fatturato mensile. PDF brandizzato, CSV o JSON.`
      : isDocumenti
        ? 'Esporta tutti i documenti contabili (preventivi + fatture) in un unico file, con tipo, stato, cliente, totale.'
        : source?.description ?? 'Entità non configurata per l\'export.';

  const label = isDashboard
    ? 'Snapshot dashboard'
    : isReport
      ? `Report${period?.label ? ` · ${period.label}` : ''}`
      : isDocumenti
        ? 'Documenti (preventivi + fatture)'
        : source?.label ?? what;

  return (
    <Modal
      title={isDashboard ? 'Esporta snapshot' : `Esporta ${label}`}
      kicker={isDashboard ? 'Dashboard · Snapshot' : 'Export dati'}
      onClose={onClose}
      size="md"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          {!isSupported ? (
            <span className="mute text-[13px]">Non disponibile</span>
          ) : (
            <button className="btn btn-primary" onClick={submit} disabled={busy}>
              <Icon name="export" size={12} />
              {busy
                ? (((isDashboard || isReport) ? dashFormat : format) === 'pdf' ? 'Generazione PDF…' : 'Esportazione…')
                : `Scarica ${((isDashboard || isReport) ? dashFormat : format).toUpperCase()}`}
            </button>
          )}
        </>
      }
    >
      {/* Header info */}
      <div className="p-3.5 rounded-2 bg-paper-2 border border-line mb-4">
        <div className="flex items-start gap-2.5">
          <div className="icon-tile sm shrink-0">
            <Icon name="export" size={14} />
          </div>
          <div className="flex-1 text-[13px] leading-[1.5]">{description}</div>
        </div>
      </div>

      {/* Formato Dashboard / Report: PDF / CSV / JSON */}
      {(isDashboard || isReport) && (
        <>
          <Field label="Formato">
            <div className="seg-control">
              <button aria-pressed={dashFormat === 'pdf'} onClick={() => setDashFormat('pdf')}>PDF</button>
              <button aria-pressed={dashFormat === 'csv'} onClick={() => setDashFormat('csv')}>CSV</button>
              <button aria-pressed={dashFormat === 'json'} onClick={() => setDashFormat('json')}>JSON</button>
            </div>
          </Field>
          <div className="mt-3 text-[13px] mute space-y-1">
            <div className="flex items-start gap-1.5">
              <Icon name="check" size={10} className="mt-0.5 text-ok" />
              <span>
                <strong>PDF</strong>: report A4 brandizzato Masamune con KPI, ricavi per divisione, progetti, alerts. Stampabile/archiviabile.
              </span>
            </div>
            <div className="flex items-start gap-1.5">
              <Icon name="check" size={10} className="mt-0.5 text-ok" />
              <span><strong>CSV</strong>: tabella metriche (Metrica/Valore/Note). Importabile in Excel/Sheets.</span>
            </div>
            <div className="flex items-start gap-1.5">
              <Icon name="check" size={10} className="mt-0.5 text-ok" />
              <span><strong>JSON</strong>: stessi dati in formato strutturato. Utile per script.</span>
            </div>
          </div>
        </>
      )}

      {/* Formato tabellari: PDF/CSV/JSON */}
      {!isDashboard && !isReport && isSupported && (
        <>
          <Field label="Formato">
            <div className="seg-control">
              <button aria-pressed={format === 'pdf'} onClick={() => setFormat('pdf')}>PDF</button>
              <button aria-pressed={format === 'csv'} onClick={() => setFormat('csv')}>CSV</button>
              <button aria-pressed={format === 'json'} onClick={() => setFormat('json')}>JSON</button>
            </div>
          </Field>

          <div className="mt-3 text-[13px] mute space-y-1">
            <div className="flex items-start gap-1.5">
              <Icon name="check" size={10} className="mt-0.5 text-ok" />
              <span>
                <strong>PDF</strong>: report A4 brandizzato Masamune con header/footer, tabella con tutti i record (multi-pagina), pronto da stampare o allegare.
              </span>
            </div>
            <div className="flex items-start gap-1.5">
              <Icon name="check" size={10} className="mt-0.5 text-ok" />
              <span>
                <strong>CSV</strong>: BOM UTF-8 (Excel italiano riconosce accenti). Importabile in Excel, Google Sheets, FattureInCloud.
              </span>
            </div>
            <div className="flex items-start gap-1.5">
              <Icon name="check" size={10} className="mt-0.5 text-ok" />
              <span>
                <strong>JSON</strong>: lista oggetti con chiavi friendly italiane. Utile per script o import in altri sistemi.
              </span>
            </div>
            <div className="flex items-start gap-1.5">
              <Icon name="alert" size={10} className="mt-0.5" />
              <span>
                I dati sono <strong>scaricati live</strong> dal server con paginazione automatica (fino a 10.000 record).
              </span>
            </div>
          </div>
        </>
      )}

      {/* Colonne preview (solo per source registrate) */}
      {source && (
        <details className="mt-4">
          <summary className="cursor-pointer text-[13px] mute select-none">
            Colonne incluse ({source.columns.length})
          </summary>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {source.columns.map((c) => (
              <span key={c.header} className="chip ghost text-[12px] mono">
                {c.header}
              </span>
            ))}
          </div>
        </details>
      )}

      {/* Result panel */}
      {result && (
        <div className="mt-4 p-3 rounded-2 bg-ok-bg text-ok border border-ok/30 text-[13px] flex items-start gap-2">
          <Icon name="check" size={12} className="mt-0.5" />
          <div>
            <strong>{result.count} record</strong> esportati in <code>{result.filename}</code>
          </div>
        </div>
      )}

      {/* Error panel */}
      {error && (
        <div className="mt-3 p-2.5 rounded-2 bg-alert-bg text-alert border border-alert/30 text-[13px] flex items-center gap-2">
          <Icon name="alert" size={12} />{error}
        </div>
      )}

      {/* Fallback panel se "what" non è mappato */}
      {!isSupported && (
        <div className="mt-3 p-3 rounded-2 bg-warn-bg text-warn border border-warn/30 text-[13px] flex items-start gap-2">
          <Icon name="alert" size={12} className="mt-0.5" />
          <div>
            L'export per <strong>{what}</strong> non è ancora configurato. Per ora l'export è disponibile per:
            <div className="mt-1 mono text-[12px]">
              {['Dashboard', 'Documenti', ...Object.keys(EXPORT_SOURCES)].join(' · ')}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
